// Sipariş Listesi ve Çoklu Sipariş'in ortak fatura bileşenleri: resmileşmiş fatura önizlemesi,
// işlem butonu tasarımı, A5 baskı ve Paraşüt yardımcıları.
import React, { useState, useEffect, useRef } from 'react';
import * as htmlToImage from 'html-to-image';
import jsPDF from 'jspdf';
import * as pdfjsLib from 'pdfjs-dist';
import CrossIconRed from './assets/images/cross_icon_red.svg';
import WhatsappIcon from './assets/images/whatsapp_icon.svg';
import DownloadIcon from './assets/images/download_icon.svg';
import PrintIcon from './assets/images/yazdir.svg';

// pdf.js worker'ı public/ altından sunulur (node_modules/pdfjs-dist/build/pdf.worker.min.js kopyası)
pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

// Resmileşmiş faturanın PDF'ini backend üzerinden Paraşüt'ten alır. Ayrı bir durum sorgusu yapılmaz:
// fatura resmileşmemişse indirme ucu bunu kendisi söyler. Paraşüt istek sınırına (429) takılırsa bekleyip tekrar dener.
// Döner: { veri: ArrayBuffer, faturaNo } veya { resmilesmemis: true }; diğer hatalarda fırlatır.
export const faturaPdfAl = async (faturaId, inline = false) => {
    const headers = { 'Authorization': `Bearer ${localStorage.getItem('token')}` };
    for (let deneme = 1; ; deneme++) {
        const response = await fetch(`/api/faturalar/${faturaId}/download${inline ? '?inline=1' : ''}`, { headers });
        if (response.ok) {
            const dosyaAdi = (response.headers.get('Content-Disposition') || '').match(/filename="fatura_(.+)\.pdf"/);
            return { veri: await response.arrayBuffer(), faturaNo: dosyaAdi ? dosyaAdi[1] : '' };
        }
        const data = await response.json().catch(() => ({}));
        const hata = data.hata || `Status ${response.status}`;
        if (response.status === 400 && hata.includes('resmileşmemiş')) return { resmilesmemis: true };
        const sinir = response.status === 429 || hata.includes('429');
        if (!sinir || deneme >= 4) {
            // status 409: e-belge oluştu ama Paraşüt PDF'i henüz hazırlamadı (önizleme kendisi tekrar dener)
            throw Object.assign(new Error(sinir ? 'Paraşüt istek sınırı aşıldı, biraz sonra tekrar deneyin' : hata), { status: response.status });
        }
        await new Promise(resolve => setTimeout(resolve, 3000 * deneme));
    }
};

// İşlem butonlarının (Gönder / İndir / Yazdır / Resmileştir ...) ortak tasarımı
export const islemButonStili = (pasif = false, bekliyor = false) => ({
    minWidth: '90px', height: '27px', background: '#F8F8F8', borderRadius: '8px', border: '1px solid #ccc',
    padding: '0 12px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px',
    // Sitede Poppins'in sadece 400 ağırlığı yüklü; boyut/ağırlık sabitlenmezse butonlar farklı yazı ile çizilir.
    fontFamily: "'Poppins', sans-serif", fontSize: '13px', fontWeight: '400', lineHeight: 1,
    flexShrink: 0, whiteSpace: 'nowrap', color: '#000',
    opacity: pasif ? 0.5 : 1, cursor: bekliyor ? 'wait' : (pasif ? 'not-allowed' : 'pointer')
});
// SVG'lerin iç boşlukları farklı olduğundan bazı ikonlar görsel olarak eşitlenmek için farklı boyutta çizilir.
const IKON_BOYUTLARI = { [DownloadIcon]: 15, [PrintIcon]: 18 };
export const islemIkonu = (src, alt) => {
    const boyut = `${IKON_BOYUTLARI[src] || 16}px`;
    return <img src={src} alt={alt} style={{ height: boyut, width: boyut, objectFit: 'contain' }} />;
};

// --- A5 BASKI ---
const A5_GENISLIK_MM = 148;
const A5_YUKSEKLIK_MM = 210;
const ETIKET_KENAR_MM = 5; // yazıcının basamadığı kenar payı: etiket çerçevesi kesilmesin
const A5_GENISLIK_PX = 1748; // 300 DPI

const gorselBoyutu = (dataUrl) => new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve({ w: img.naturalWidth, h: img.naturalHeight });
    img.onerror = reject;
    img.src = dataUrl;
});

// Ekrandaki etiket düğümünü (EtiketSablonu çıktısı) baskı görseline çevirir.
// Etiket sadece Arial kullanır; web fontları gömülmez. Gömülürse (Poppins + tarayıcı eklentilerinin eklediği fontlar)
// ara SVG adresi Chrome'un 2 MB URL sınırını aşabiliyor ve görsel ERR_INVALID_URL ile yüklenemiyor.
export const etiketDugumGorseli = async (dugum) => {
    const dataUrl = await htmlToImage.toPng(dugum, { pixelRatio: 3, backgroundColor: 'white', skipFonts: true });
    return { tip: 'PNG', dataUrl, ...(await gorselBoyutu(dataUrl)) };
};

// pdf.js belgesinin her sayfasını A5 genişliğinde (300 DPI) görsele çevirir
export const pdfSayfaGorselleri = async (doc) => {
    const sayfalar = [];
    for (let no = 1; no <= doc.numPages; no++) {
        const page = await doc.getPage(no);
        const viewport = page.getViewport({ scale: A5_GENISLIK_PX / page.getViewport({ scale: 1 }).width });
        const canvas = document.createElement('canvas');
        canvas.width = Math.round(viewport.width);
        canvas.height = Math.round(viewport.height);
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = 'white';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        await page.render({ canvasContext: ctx, viewport }).promise;
        sayfalar.push({ tip: 'JPEG', dataUrl: canvas.toDataURL('image/jpeg', 0.95), w: canvas.width, h: canvas.height });
    }
    return sayfalar;
};

// Yüzleri (etiket PNG / fatura sayfası JPEG / null = boş sayfa) A5 dikey tek PDF'te birleştirip yazdırma penceresini açar.
// ciftTarafli=false ise tek yönlü baskı istenir.
let yazdirIframe = null;
export const a5Yazdir = (yuzler, ciftTarafli = false) => {
    const pdf = new jsPDF({ orientation: 'p', unit: 'mm', format: 'a5' });
    pdf.viewerPreferences({ Duplex: ciftTarafli ? 'DuplexFlipLongEdge' : 'Simplex', PrintScaling: 'None' });
    yuzler.forEach((yuz, sira) => {
        if (sira > 0) pdf.addPage('a5', 'p');
        if (!yuz) return;
        if (yuz.tip === 'PNG') {
            // Etiket: sayfanın üstünde, kenar paylı
            const w = A5_GENISLIK_MM - ETIKET_KENAR_MM * 2;
            pdf.addImage(yuz.dataUrl, 'PNG', ETIKET_KENAR_MM, ETIKET_KENAR_MM, w, w * yuz.h / yuz.w);
        } else {
            // Fatura sayfası: oranı korunarak sayfaya sığdırılır, ortalanır
            const olcek = Math.min(A5_GENISLIK_MM / yuz.w, A5_YUKSEKLIK_MM / yuz.h);
            const w = yuz.w * olcek, h = yuz.h * olcek;
            pdf.addImage(yuz.dataUrl, 'JPEG', (A5_GENISLIK_MM - w) / 2, (A5_YUKSEKLIK_MM - h) / 2, w, h);
        }
    });

    // Gizli iframe'de açıp yazdırma penceresini getir
    const url = pdf.output('bloburl');
    if (yazdirIframe) {
        window.URL.revokeObjectURL(yazdirIframe.src);
        yazdirIframe.remove();
    }
    const iframe = document.createElement('iframe');
    // display:none olursa Chrome PDF'i yüklemez; görünmez ama yerleşik tutulur
    iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:1px;height:1px;border:0;opacity:0;';
    iframe.onload = () => setTimeout(() => {
        try {
            iframe.contentWindow.focus();
            iframe.contentWindow.print();
        } catch (e) {
            window.open(url, '_blank'); // tarayıcı iframe'den yazdırmaya izin vermezse PDF yeni sekmede açılır
        }
    }, 300);
    iframe.src = url;
    document.body.appendChild(iframe);
    yazdirIframe = iframe;
};

// Resmileşmiş faturanın e-fatura/e-arşiv PDF önizlemesi (Paraşüt'ten backend üzerinden alınır).
// Tarayıcının PDF görüntüleyicisi yerine pdf.js ile çizilir: sayfa kutuya tam sığar, çok sayfada sekmeler çıkar.
// iptalEdildi: fatura Paraşüt'te iptal edilmiş; İptal Et sistemdeki bağlantıyı kaldırır
export default function FaturaOnizleme({ faturaId, onIndir, telefon, onIptal, iptalEdiliyor, iptalEdildi = false }) {
    const [pdfDoc, setPdfDoc] = useState(null);
    const [sayfa, setSayfa] = useState(1);
    const [hata, setHata] = useState(null);
    const [deneme, setDeneme] = useState(0);
    const canvasRef = useRef(null);
    const kutuRef = useRef(null);

    // Resmileştirmeden sonra Paraşüt e-arşivi GİB'e raporlarken PDF dakikalarca hazır olmayabilir (409);
    // bu sürede önizleme 20 sn arayla kendisi tekrar dener (en fazla ~15 dk)
    const otomatikDeneme = useRef(0);
    useEffect(() => { otomatikDeneme.current = 0; }, [faturaId]);

    useEffect(() => {
        let iptal = false;
        let doc = null;
        let zamanlayici = null;
        setPdfDoc(null);
        setSayfa(1);
        setHata(null);
        (async () => {
            try {
                const sonuc = await faturaPdfAl(faturaId, true);
                if (sonuc.resmilesmemis) throw new Error('Bu fatura henüz resmileşmemiş, PDF mevcut değil.');
                doc = await pdfjsLib.getDocument({ data: new Uint8Array(sonuc.veri) }).promise;
                if (iptal) return;
                setPdfDoc(doc);
            } catch (error) {
                if (iptal) return;
                if (error.status === 409 && otomatikDeneme.current < 45) {
                    otomatikDeneme.current += 1;
                    setHata('E-belge PDF\'i Paraşüt\'te hazırlanıyor (GİB raporlaması birkaç dakika sürebilir). Otomatik tekrar denenecek...');
                    zamanlayici = setTimeout(() => setDeneme(d => d + 1), 20000);
                } else {
                    setHata(error.message);
                }
            }
        })();
        return () => {
            iptal = true;
            clearTimeout(zamanlayici);
            if (doc) doc.destroy();
        };
    }, [faturaId, deneme]);

    useEffect(() => {
        if (!pdfDoc || !canvasRef.current || !kutuRef.current) return;
        let gorev = null;
        let iptal = false;
        (async () => {
            const page = await pdfDoc.getPage(sayfa);
            if (iptal) return;
            const dpr = window.devicePixelRatio || 1;
            const genislik = kutuRef.current.clientWidth;
            const viewport = page.getViewport({ scale: (genislik / page.getViewport({ scale: 1 }).width) * dpr });
            const canvas = canvasRef.current;
            canvas.width = viewport.width;
            canvas.height = viewport.height;
            gorev = page.render({ canvasContext: canvas.getContext('2d'), viewport });
            gorev.promise.catch(() => {}); // sayfa değişiminde iptal edilen çizim
        })();
        return () => {
            iptal = true;
            if (gorev) gorev.cancel();
        };
    }, [pdfDoc, sayfa]);

    // Faturanın tüm sayfalarını alt alta tek resim yapıp panoya kopyalar ve WhatsApp'ta müşterinin sohbetini açar.
    // Tarayıcı panoya PDF dosyası yazamadığı için (sadece metin/resim) fatura resim olarak yapıştırılır.
    const [gonderiliyor, setGonderiliyor] = useState(false);
    const handleWhatsappGonder = async () => {
        const numara = (telefon || '').replace(/[^0-9]/g, '');
        if (!numara) { alert('Gönderilecek telefon numarası boş.'); return; }
        const resimOlustur = async () => {
            const sayfalar = [];
            for (let n = 1; n <= pdfDoc.numPages; n++) {
                const page = await pdfDoc.getPage(n);
                const viewport = page.getViewport({ scale: 2 });
                const canvas = document.createElement('canvas');
                canvas.width = Math.round(viewport.width);
                canvas.height = Math.round(viewport.height);
                const ctx = canvas.getContext('2d');
                ctx.fillStyle = 'white';
                ctx.fillRect(0, 0, canvas.width, canvas.height);
                await page.render({ canvasContext: ctx, viewport }).promise;
                sayfalar.push(canvas);
            }
            const birlesik = document.createElement('canvas');
            birlesik.width = Math.max(...sayfalar.map(c => c.width));
            birlesik.height = sayfalar.reduce((toplam, c) => toplam + c.height, 0);
            const ctx = birlesik.getContext('2d');
            ctx.fillStyle = 'white';
            ctx.fillRect(0, 0, birlesik.width, birlesik.height);
            let y = 0;
            sayfalar.forEach(c => { ctx.drawImage(c, 0, y); y += c.height; });
            return new Promise((resolve, reject) => birlesik.toBlob(
                blob => blob ? resolve(blob) : reject(new Error('Görsel oluşturulamadı.')), 'image/png'));
        };
        setGonderiliyor(true);
        try {
            // Resim hazırlanırken tıklama izni düşmesin diye panoya söz (promise) verilir
            await navigator.clipboard.write([new ClipboardItem({ 'image/png': resimOlustur() })]);
            window.location.href = `whatsapp://send?phone=90${numara}`;
        } catch (error) {
            console.error('Fatura WhatsApp gönderim hatası:', error);
            alert(`Fatura gönderilemedi: ${error.message}`);
        } finally {
            setGonderiliyor(false);
        }
    };

    // E-faturayı A5 dikey, tek yönlü yazdırır
    const [yazdiriliyor, setYazdiriliyor] = useState(false);
    const handleYazdir = async () => {
        setYazdiriliyor(true);
        try {
            a5Yazdir(await pdfSayfaGorselleri(pdfDoc), false);
        } catch (error) {
            console.error('Fatura yazdırma hatası:', error);
            alert(`Fatura yazdırılamadı: ${error.message}`);
        } finally {
            setYazdiriliyor(false);
        }
    };

    const sayfaSayisi = pdfDoc ? pdfDoc.numPages : 0;
    const butonStili = { ...islemButonStili(), width: 'auto' };

    return (
        <div style={{ fontFamily: 'Poppins, sans-serif' }} onClick={(e) => e.stopPropagation()}>
            {iptalEdildi && (
                <div style={{ marginBottom: '8px', padding: '8px 10px', background: '#f8d7da', border: '1px solid #dc3545', borderRadius: '4px', color: '#842029', fontSize: '12px' }}>
                    <strong>Bu fatura Paraşüt'te iptal edilmiş.</strong> "İptal Et" ile sistemde de iptal edip yeniden fatura kesebilirsiniz.
                </div>
            )}
            <div style={{ display: 'flex', gap: '12px', marginBottom: '8px', flexWrap: 'wrap' }}>
                <button
                    type="button"
                    style={islemButonStili(!pdfDoc || gonderiliyor, gonderiliyor)}
                    disabled={!pdfDoc || gonderiliyor}
                    onClick={handleWhatsappGonder}
                    title="Faturayı panoya kopyala ve WhatsApp'ta müşterinin sohbetini aç (Ctrl+V ile yapıştırın)"
                >
                    {islemIkonu(WhatsappIcon, 'Gönder')}
                    <span>{gonderiliyor ? 'Hazırlanıyor...' : 'Gönder'}</span>
                </button>
                <button type="button" style={islemButonStili()} onClick={onIndir}>
                    {islemIkonu(DownloadIcon, 'İndir')}
                    <span>İndir</span>
                </button>
                <button
                    type="button"
                    style={islemButonStili(!pdfDoc || yazdiriliyor, yazdiriliyor)}
                    disabled={!pdfDoc || yazdiriliyor}
                    onClick={handleYazdir}
                    title="E-faturayı A5 dikey, tek yönlü yazdır"
                >
                    {islemIkonu(PrintIcon, 'Yazdır')}
                    <span>{yazdiriliyor ? 'Hazırlanıyor...' : 'Yazdır'}</span>
                </button>
                {onIptal && (
                    <button
                        type="button"
                        style={islemButonStili(iptalEdiliyor, iptalEdiliyor)}
                        disabled={iptalEdiliyor}
                        onClick={onIptal}
                        title={`Faturayı iptal et (sadece son kullanıcı (11111111111) faturaları, fatura tarihinden itibaren 7 gün içinde)`}
                    >
                        <img src={CrossIconRed} alt="İptal Et" style={{ height: '22px', margin: '0 -3px' }} />
                        <span>{iptalEdiliyor ? 'İptal...' : 'İptal Et'}</span>
                    </button>
                )}
            </div>
            {sayfaSayisi > 1 && (
                <div style={{ display: 'flex', gap: '4px', marginBottom: '6px' }}>
                    {Array.from({ length: sayfaSayisi }, (_, i) => i + 1).map(n => (
                        <button
                            key={n}
                            onClick={() => setSayfa(n)}
                            style={{
                                padding: '2px 12px', fontSize: '12px', fontFamily: 'Poppins, sans-serif', cursor: 'pointer', borderRadius: '4px',
                                border: n === sayfa ? '1px solid #555' : '1px solid #ddd',
                                background: n === sayfa ? '#eee' : '#fff',
                                fontWeight: n === sayfa ? '600' : '400'
                            }}
                        >
                            {n}
                        </button>
                    ))}
                </div>
            )}
            <div ref={kutuRef} style={{ border: '1px solid #e0e0e0', borderRadius: '4px', background: '#fff', overflow: 'hidden', minHeight: pdfDoc ? 0 : '120px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {hata ? (
                    <div style={{ textAlign: 'center', fontSize: '12px', color: '#666', padding: '10px' }}>
                        <p style={{ margin: '0 0 8px 0' }}>{hata}</p>
                        <button style={{ ...butonStili, width: 'auto', margin: '0 auto' }} onClick={() => setDeneme(d => d + 1)}>Tekrar Dene</button>
                    </div>
                ) : !pdfDoc ? (
                    <span style={{ fontSize: '12px', color: '#666' }}>Önizleme yükleniyor...</span>
                ) : null}
                <canvas ref={canvasRef} style={{ width: '100%', display: pdfDoc && !hata ? 'block' : 'none' }} />
            </div>
        </div>
    );
}

// Paraşüt'teki faturayı resmileştirir (e-fatura mükellefine e-fatura, diğerlerine e-arşiv) - GERİ ALINAMAZ
// Resmileştirme işini Paraşüt'e verir ve bitmesini tarayıcıda bekler (sunucuda beklenmez: gunicorn 30 sn timeout).
// Döner: { belge_turu, devamEdiyor } - devamEdiyor: 2 dk içinde bitmedi, Paraşüt arka planda tamamlayabilir.
export const parasutResmilestir = async (faturaId) => {
    const headers = { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` };
    const response = await fetch(`/api/faturalar/${faturaId}/resmilestir`, { method: 'POST', headers });
    if (response.status === 401) throw new Error('Oturum süresi doldu.');
    const data = await response.json();
    if (!response.ok) throw new Error(data.hata || 'Resmileştirme başarısız');

    const bitis = Date.now() + 120000;
    while (Date.now() < bitis) {
        await new Promise(r => setTimeout(r, 3000)); // Paraşüt istek sınırı için seyrek sorgulanır
        let is = null;
        try {
            const r = await fetch(`/api/parasut/isler/${data.job_id}`, { headers, cache: 'no-cache' });
            if (r.ok) is = await r.json();
        } catch (e) {
            // geçici bağlantı hatası: bir sonraki turda tekrar sorulur
        }
        if (is?.durum === 'done') return { belge_turu: data.belge_turu, devamEdiyor: false };
        if (is?.durum === 'error') {
            throw new Error(`${data.belge_turu} oluşturulamadı` + (is.hatalar?.length ? `: ${is.hatalar.join('; ')}` : '.'));
        }
    }
    return { belge_turu: data.belge_turu, devamEdiyor: true };
};

// Taslak (resmileşmemiş) faturanın e-arşiv / e-fatura önizleme PDF'ini (fatura no: ONIZLEME) yeni sekmede açar.
// Paraşüt PDF'i iş (trackable job) olarak hazırlar; bitene kadar beklenir. Sekme tıklama anında açılır ki
// tarayıcı açılır pencere engellemesine takılmasın; adres PDF hazır olunca verilir.
export const taslakOnizlemeAc = async (faturaId) => {
    const sekme = window.open('', '_blank');
    if (sekme) sekme.document.write('<p style="font-family:sans-serif">Fatura önizlemesi hazırlanıyor...</p>');
    const headers = { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` };
    try {
        const response = await fetch(`/api/faturalar/${faturaId}/taslak-onizleme`, { method: 'POST', headers });
        const data = await response.json();
        if (!response.ok || !data.job_id) throw new Error(data.hata || `Sunucu hatası (Kod ${response.status})`);

        let url = null;
        const bitis = Date.now() + 60000;
        while (!url && Date.now() < bitis) {
            await new Promise(r => setTimeout(r, 1500));
            let is = null;
            try {
                const r = await fetch(`/api/parasut/isler/${data.job_id}`, { headers, cache: 'no-cache' });
                if (r.ok) is = await r.json();
            } catch (e) {
                // geçici bağlantı hatası: bir sonraki turda tekrar sorulur
            }
            if (is?.durum === 'error') throw new Error(is.hatalar?.length ? is.hatalar.join('; ') : 'Önizleme oluşturulamadı.');
            if (is?.durum === 'done') {
                if (!is.url) throw new Error('Paraşüt önizleme adresi döndürmedi.');
                url = is.url;
            }
        }
        if (!url) throw new Error('Paraşüt önizlemeyi zamanında hazırlamadı, tekrar deneyin.');
        if (sekme) sekme.location.href = url;
        else window.open(url, '_blank');
    } catch (error) {
        if (sekme) sekme.close();
        throw error;
    }
};
